import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { effectiveLeadOptions, nextLeadOptions, type LeadOptionKind } from "../features/leads/lead-options";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  BellOff,
  Calendar,
  Check,
  FileText,
  Inbox,
  Instagram,
  List,
  Lock,
  Mail,
  Monitor,
  Moon,
  Plug,
  Sun,
  Archive,
  Building2,
  CircleSlash,
  Compass,
  Layers,
  ListPlus,
  Pencil,
  Tag as TagIcon,
  Target,
  UserRound,
} from "lucide-react";
import { useStore } from "../lib/store";
import {
  NOTICE_CATEGORIES,
  NOTICE_CATEGORY_LABEL,
  type NoticeCategory,
} from "../features/notifications/feed";
import { readPreference, type NotificationPreferences } from "../features/preferences/contracts";
import { useProfileIdentity } from "../app/useProfileIdentity";
import { avatarGradient, avatarInitials } from "../ui/avatar-identity";
import { Button } from "../ui/Button";
import { Field, TextArea, TextInput } from "../ui/Field";
import { Dialog } from "../ui/Dialog";
import { Menu } from "../ui/Menu";
import { Select } from "../ui/Select";
import { Switch } from "../ui/Switch";
import {
  applyThemeChoice,
  persistThemeChoice,
  readThemeChoice,
  type ThemeChoice,
} from "../app/theme";
import { appPath } from "../app/routes";
import { isDemoMode } from "../lib/supabase";
import { showFeedback } from "../lib/feedback";
import { ConfirmDialog } from "./PerfilDialogs";
import { FormFieldsEditor } from "../features/leads/FormFieldsEditor";
import { money } from "./lista-popovers";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { ColorMenu } from "../ui/ColorMenu";
import "./configuracoes.css";

/**
 * Configurações — porte da superfície `cs-*` do Zênite: coluna de seções à
 * esquerda e um painel por assunto à direita, cada um com rodapé próprio de
 * "Descartar alterações / Salvar".
 *
 * Perfil, notificações e orientação da IA são preferências do dono
 * (`features/preferences`); meta é comando financeiro; cadastros usam os
 * comandos de catálogo já testados. O que o domínio ainda não permite aparece
 * desabilitado com o motivo, nunca como campo que não salva (§18).
 */

type TabId = "perfil" | "notificacoes" | "cadastros" | "integracoes" | "metas" | "ia";

interface TabDef {
  id: TabId;
  label: string;
  title: string;
  sub: string;
  icon: ReactNode;
  /** Separador do protótipo entre as duas famílias de seção. */
  dividerBefore?: boolean;
}

const TABS: TabDef[] = [
  {
    id: "perfil",
    label: "Perfil",
    title: "Perfil",
    sub: "Seus dados e a aparência do CosmosLeads.",
    icon: <UserRound size={16} aria-hidden="true" />,
  },
  {
    id: "notificacoes",
    label: "Notificações",
    title: "Notificações",
    sub: "Escolha o que merece sua atenção e onde receber os avisos.",
    icon: <Bell size={16} aria-hidden="true" />,
  },
  {
    id: "cadastros",
    label: "Cadastros",
    title: "Cadastros",
    sub: "Organize as opções usadas nos seus leads e clientes.",
    icon: <List size={16} aria-hidden="true" />,
    dividerBefore: true,
  },
  {
    id: "integracoes",
    label: "Integrações",
    title: "Integrações",
    sub: "Canais e serviços disponíveis na demonstração.",
    icon: <Plug size={16} aria-hidden="true" />,
  },
  {
    id: "metas",
    label: "Metas",
    title: "Metas",
    sub: "Defina o objetivo de receita para acompanhar o trimestre.",
    icon: <Target size={16} aria-hidden="true" />,
  },
  {
    id: "ia",
    label: "Inteligência artificial",
    title: "Inteligência artificial",
    sub: "Prepare a orientação das sugestões e acesse seus modelos.",
    icon: <CosmosAiIcon size={16} />,
  },
];

function isTab(value: string | undefined): value is TabId {
  return TABS.some((tab) => tab.id === value);
}

export function ConfiguracoesPage() {
  const params = useParams();
  const navigate = useNavigate();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const tab: TabId = isTab(params["*"]?.split("/")[0]) ? (params["*"]!.split("/")[0] as TabId) : "perfil";
  const current = TABS.find((item) => item.id === tab)!;

  // Entrar na seção põe o foco no início dela (AGENTS §17).
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [tab]);

  return (
    <div className="cs-layout">
      <aside className="cs-side">
        <h2>Configurações</h2>
        <nav aria-label="Seções das configurações">
          {TABS.map((item) => (
            <div key={item.id} className="cs-side-item">
              {item.dividerBefore && <hr />}
              <button
                type="button"
                aria-current={item.id === tab ? "page" : undefined}
                onClick={() => navigate(`${appPath("configuracoes")}/${item.id}`)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            </div>
          ))}
        </nav>
        <p>
          <Monitor size={15} aria-hidden="true" />
          <span>Preferências deste navegador: o tema fica aqui; o resto acompanha sua conta.</span>
        </p>
      </aside>

      <main className="cs-main">
        <div className="pagehead">
          <div>
            <h1 ref={titleRef} tabIndex={-1}>
              {current.title}
            </h1>
            <p className="sub">{current.sub}</p>
          </div>
          <div className="actions z-inline">
            <Button icon={<ArrowLeft size={15} aria-hidden="true" />} onClick={() => navigate(appPath())}>
              Voltar ao CRM
            </Button>
          </div>
        </div>
        <div className="cs-content">
          {tab === "perfil" && <PerfilTab />}
          {tab === "notificacoes" && <NotificacoesTab />}
          {tab === "cadastros" && <CadastrosTab />}
          {tab === "integracoes" && <IntegracoesTab />}
          {tab === "metas" && <MetasTab />}
          {tab === "ia" && <IaTab />}
        </div>
      </main>
    </div>
  );
}

/** Rodapé comum: estado do salvamento à esquerda, ações à direita. */
function FormFoot({
  dirty,
  saving,
  onDiscard,
  saveLabel,
}: {
  dirty: boolean;
  saving: boolean;
  onDiscard: () => void;
  saveLabel: string;
}) {
  return (
    <footer className="cs-form-foot">
      <span className={dirty ? "cs-save-status is-dirty" : "cs-save-status"} role="status">
        {saving ? "Salvando…" : dirty ? "Alterações não salvas" : "Tudo salvo"}
      </span>
      <div>
        <Button type="button" onClick={onDiscard} disabled={!dirty || saving}>
          Descartar alterações
        </Button>
        <Button
          type="submit"
          variant="primary"
          icon={<Check size={15} aria-hidden="true" />}
          disabled={!dirty}
          loading={saving}
        >
          {saveLabel}
        </Button>
      </div>
    </footer>
  );
}

const TIME_ZONES = [
  { value: "America/Sao_Paulo", label: "Brasília · São Paulo" },
  { value: "America/Manaus", label: "Manaus" },
  { value: "America/Rio_Branco", label: "Rio Branco" },
  { value: "America/Noronha", label: "Fernando de Noronha" },
];

function PerfilTab() {
  const store = useStore();
  const identity = useProfileIdentity();
  const saved = readPreference(store.userPreferences ?? [], "profile").value;
  const [form, setForm] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [theme, setTheme] = useState<ThemeChoice>(() => readThemeChoice());
  const [restoring, setRestoring] = useState(false);

  useEffect(() => setForm(saved), [saved.displayName, saved.agencyName, saved.timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    form.displayName !== saved.displayName ||
    form.agencyName !== saved.agencyName ||
    form.timeZone !== saved.timeZone;
  const name = form.displayName.trim() || identity.name;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const result = await store.setPreference({ key: "profile", value: form });
    setSaving(false);
    if (!result.ok) setError(result.error.message);
  };

  const chooseTheme = (choice: ThemeChoice) => {
    setTheme(choice);
    persistThemeChoice(choice);
    applyThemeChoice(choice);
  };

  return (
    <>
      <section className="cs-panel">
        <div className="cs-identity">
          <span className="cs-avatar" style={{ background: avatarGradient(name) }} aria-hidden="true">
            {avatarInitials(name)}
          </span>
          <div>
            <h2>{name}</h2>
            <p>
              {form.agencyName.trim() || "Sem agência declarada"}
              <span>·</span>
              {identity.email ?? "sem sessão neste ambiente"}
            </p>
          </div>
          <span className="cs-local">
            <Monitor size={15} aria-hidden="true" /> Perfil da conta
          </span>
        </div>
        <form className="cs-form" onSubmit={submit}>
          <section className="cs-setting-row">
            <div className="cs-section-intro">
              <h2>Dados do perfil</h2>
              <p>Como você se identifica no seu espaço de trabalho.</p>
            </div>
            <div className="cs-fields">
              <Field label="Nome">
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={form.displayName}
                    maxLength={120}
                    onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                  />
                )}
              </Field>
              <Field
                label="E-mail"
                help="Vem da sua sessão e muda na autenticação, não aqui."
              >
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    value={identity.email ?? ""}
                    placeholder="sem sessão neste ambiente"
                    readOnly
                    disabled
                  />
                )}
              </Field>
              <Field label="Empresa / agência">
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={form.agencyName}
                    maxLength={120}
                    onChange={(event) => setForm({ ...form, agencyName: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Fuso horário">
                {({ id }) => (
                  <Select
                    id={id}
                    size="field"
                    options={TIME_ZONES}
                    value={form.timeZone}
                    label="Fuso horário"
                    onChange={(value) => setForm({ ...form, timeZone: value })}
                  />
                )}
              </Field>
              <p className="cs-field-note">
                As datas do produto são apresentadas neste fuso; o que fica gravado continua em UTC.
              </p>
            </div>
          </section>
          {error && (
            <p className="cs-form-error" role="alert">
              {error}
            </p>
          )}
          <FormFoot dirty={dirty} saving={saving} onDiscard={() => setForm(saved)} saveLabel="Salvar perfil" />
        </form>
      </section>

      <section className="cs-panel cs-setting-row cs-appearance">
        <div className="cs-section-intro">
          <h2>Aparência</h2>
          <p>Escolha um tema ou acompanhe a preferência do seu sistema.</p>
        </div>
        <div>
          <div className="cs-theme-options" role="group" aria-label="Tema da interface">
            {(
              [
                { id: "light", label: "Claro", icon: <Sun size={14} aria-hidden="true" /> },
                { id: "dark", label: "Escuro", icon: <Moon size={14} aria-hidden="true" /> },
                { id: "system", label: "Sistema", icon: <Monitor size={14} aria-hidden="true" /> },
              ] as Array<{ id: ThemeChoice; label: string; icon: ReactNode }>
            ).map((option) => (
              <button
                key={option.id}
                type="button"
                className="cs-theme-option"
                aria-pressed={theme === option.id}
                onClick={() => chooseTheme(option.id)}
              >
                <span className={`cs-theme-sample ${option.id}`} aria-hidden="true">
                  <i />
                  <b />
                  <em />
                </span>
                <span>
                  {option.icon}
                  {option.label}
                  {theme === option.id && <Check size={14} aria-hidden="true" />}
                </span>
              </button>
            ))}
          </div>
          <p className="cs-field-note">
            Aplicado imediatamente neste navegador. É preferência do aparelho, não da conta.
          </p>
        </div>
      </section>
      {isDemoMode && (
        <section className="cs-panel cs-setting-row">
          <div className="cs-section-intro">
            <h2>Dados de demonstração</h2>
            <p>
              Devolve o conteúdo de exemplo ao estado inicial. Só existe no modo de
              demonstração, e o que você criou neste navegador é perdido.
            </p>
          </div>
          <div>
            <Button variant="danger" onClick={() => setRestoring(true)}>
              Restaurar dados de demonstração
            </Button>
          </div>
        </section>
      )}
      {restoring && (
        <ConfirmDialog
          title="Restaurar dados de demonstração"
          question="Todo o conteúdo deste navegador volta ao exemplo inicial: cadastros, conversas, tarefas, contratos e preferências. Não há como desfazer."
          confirmLabel="Restaurar"
          danger
          onConfirm={() => {
            store.resetDemo();
            setRestoring(false);
            showFeedback("Dados de demonstração restaurados.", "success");
          }}
          onClose={() => setRestoring(false)}
        />
      )}
    </>
  );
}

type ReceiveMode = "both" | "center" | "paused";

const MODES: Array<{ id: ReceiveMode; title: string; hint: string; icon: ReactNode }> = [
  {
    id: "both",
    title: "Central e popups",
    hint: "Veja os avisos também enquanto trabalha.",
    icon: <Bell size={17} aria-hidden="true" />,
  },
  {
    id: "center",
    title: "Somente na central",
    hint: "Consulte os avisos quando preferir.",
    icon: <List size={17} aria-hidden="true" />,
  },
  {
    id: "paused",
    title: "Recebimento pausado",
    hint: "Seu histórico e suas preferências ficam preservados.",
    icon: <BellOff size={17} aria-hidden="true" />,
  },
];

const POSITIONS: Array<{ id: NotificationPreferences["bannerPosition"]; label: string }> = [
  { id: "top-right", label: "Superior direito" },
  { id: "top-left", label: "Superior esquerdo" },
  { id: "bottom-right", label: "Inferior direito" },
  { id: "bottom-left", label: "Inferior esquerdo" },
  { id: "bottom-center", label: "Inferior central" },
];

/** O modo é leitura das próprias marcas: nenhuma preferência extra é inventada. */
function modeOf(preferences: NotificationPreferences): ReceiveMode {
  const anyCategory = NOTICE_CATEGORIES.some((category) => preferences.categories[category] !== false);
  if (!anyCategory) return "paused";
  const anyBanner = NOTICE_CATEGORIES.some((category) => preferences.banners[category] === true);
  return anyBanner ? "both" : "center";
}

function NotificacoesTab() {
  const store = useStore();
  const saved = readPreference(store.userPreferences ?? [], "notifications").value;
  const [busy, setBusy] = useState(false);
  const mode = modeOf(saved);

  const save = async (value: NotificationPreferences) => {
    setBusy(true);
    await store.setPreference({ key: "notifications", value });
    setBusy(false);
  };

  const chooseMode = (next: ReceiveMode) => {
    const categories = Object.fromEntries(
      NOTICE_CATEGORIES.map((category) => [category, next !== "paused"]),
    ) as NotificationPreferences["categories"];
    const banners = Object.fromEntries(
      NOTICE_CATEGORIES.map((category) => [category, next === "both"]),
    ) as NotificationPreferences["banners"];
    void save({ ...saved, categories, banners });
  };

  const toggle = (kind: "categories" | "banners", category: NoticeCategory, value: boolean) => {
    void save({ ...saved, [kind]: { ...saved[kind], [category]: value } });
  };

  return (
    <section className="cs-panel">
      <div className="cs-setting-row">
        <div className="cs-section-intro">
          <h2>Recebimento</h2>
          <p>Escolha como acompanhar as atividades do CRM.</p>
        </div>
        <div className="cs-modes" role="radiogroup" aria-label="Forma de recebimento">
          {MODES.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={mode === option.id}
              disabled={busy}
              onClick={() => chooseMode(option.id)}
            >
              {option.icon}
              <span>
                <strong>{option.title}</strong>
                <small>{option.hint}</small>
              </span>
              <i aria-hidden="true" />
            </button>
          ))}
        </div>
      </div>

      <div className="cs-setting-row">
        <div className="cs-section-intro">
          <h2>Posição dos popups</h2>
          <p>Uma prévia de onde os avisos aparecem na tela.</p>
        </div>
        <div className="cs-positions" role="radiogroup" aria-label="Posição dos avisos flutuantes">
          {POSITIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              className="cs-position"
              aria-checked={saved.bannerPosition === option.id}
              disabled={busy || mode !== "both"}
              title={mode === "both" ? undefined : "Os popups estão desligados no modo escolhido acima."}
              onClick={() => void save({ ...saved, bannerPosition: option.id })}
            >
              <span className={`cs-position-mini ${option.id}`} aria-hidden="true">
                <i />
              </span>
              <span className="cs-position-label">
                <i aria-hidden="true" />
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="cs-setting-row">
        <div className="cs-section-intro">
          <h2>Atividades</h2>
          <p>Defina o que aparece no sininho e o que pode abrir um popup.</p>
        </div>
        <div>
          <table className="cs-notif-table">
            <thead>
              <tr>
                <th scope="col">Notificar quando</th>
                <th scope="col">No sininho</th>
                <th scope="col">Popup</th>
              </tr>
            </thead>
            <tbody>
              {NOTICE_CATEGORIES.map((category) => {
                const visible = saved.categories[category] !== false;
                return (
                  <tr key={category}>
                    <th scope="row">
                      <strong>{NOTICE_CATEGORY_LABEL[category]}</strong>
                    </th>
                    <td>
                      <Switch
                        checked={visible}
                        disabled={busy}
                        label={`Mostrar ${NOTICE_CATEGORY_LABEL[category]} no sininho`}
                        onChange={(value) => toggle("categories", category, value)}
                      />
                    </td>
                    <td>
                      <Switch
                        checked={saved.banners[category] === true}
                        disabled={busy || !visible}
                        disabledReason={visible ? undefined : "A categoria está oculta no sininho."}
                        label={`Abrir popup de ${NOTICE_CATEGORY_LABEL[category]}`}
                        onChange={(value) => toggle("banners", category, value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="cs-field-note">
            Preferências salvas ao alterar. Desativar no sininho oculta a categoria ali; os registros continuam na
            página de Notificações.
          </p>
        </div>
      </div>
    </section>
  );
}

type CatalogId = "tags" | "origens" | "segmentos" | "motivos" | "classificacoes" | "campos";

function CadastrosTab() {
  const store = useStore();
  const [catalog, setCatalog] = useState<CatalogId>("tags");
  /** Eixo cujas opções estão abertas; vazio mostra só a lista de eixos. */
  const [eixo, setEixo] = useState("");
  const funnelId = store.funnels.find((funnel) => !funnel.archived)?.id ?? "";

  const tags = store.tags.filter((tag) => !tag.archived);
  const archivedTags = store.tags.filter((tag) => tag.archived);
  const reasons = store.lossReasons.filter((reason) => !reason.archived);
  const archivedReasons = store.lossReasons.filter((reason) => reason.archived);
  const leadOptions = readPreference(store.userPreferences ?? [], "leadOptions").value;
  const sourcesInUse = useMemo(() => store.leads.map((lead) => lead.source), [store.leads]);
  const segmentsInUse = useMemo(() => store.leads.map((lead) => lead.segment ?? ""), [store.leads]);
  const sources = effectiveLeadOptions(leadOptions, sourcesInUse, "sources");
  const segments = effectiveLeadOptions(leadOptions, segmentsInUse, "segments");
  const classifications = (store.classificationDefinitions ?? []).filter((item) => !item.archived);
  const archivedClassifications = (store.classificationDefinitions ?? []).filter(
    (item) => item.archived,
  );
  const fields = store.leadFields.filter((field) => !field.archived);

  const CATALOGS: Array<{ id: CatalogId; label: string; count: number; icon: ReactNode }> = [
    { id: "tags", label: "Tags", count: tags.length, icon: <TagIcon size={15} aria-hidden="true" /> },
    { id: "origens", label: "Origens", count: sources.length, icon: <Compass size={15} aria-hidden="true" /> },
    { id: "segmentos", label: "Segmentos", count: segments.length, icon: <Building2 size={15} aria-hidden="true" /> },
    { id: "motivos", label: "Motivos de perda", count: reasons.length, icon: <CircleSlash size={15} aria-hidden="true" /> },
    {
      id: "classificacoes",
      label: "Classificações",
      count: classifications.length,
      icon: <Layers size={15} aria-hidden="true" />,
    },
    { id: "campos", label: "Campos personalizados", count: fields.length, icon: <ListPlus size={15} aria-hidden="true" /> },
  ];

  return (
    <section className="cs-panel cs-catalogs">
      <nav aria-label="Cadastros">
        {CATALOGS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-current={item.id === catalog ? "true" : undefined}
            onClick={() => setCatalog(item.id)}
          >
            {item.icon}
            <span>{item.label}</span>
            <b>{item.count}</b>
          </button>
        ))}
      </nav>
      <div className="cs-catalog-editor">
        {catalog === "tags" && (
          <CatalogEditor
            title="Tags"
            description="Marcadores para organizar pessoas e oportunidades."
            options={tags.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }))}
            archived={archivedTags.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }))}
            onCreate={(name) => store.addTag(name, undefined, funnelId)}
            onRename={(id, name) => store.renameConfig("tag", id, name)}
            onArchive={(id) => store.toggleArchivedConfig("tag", id)}
            onRestore={(id) => store.toggleArchivedConfig("tag", id)}
            onReorder={(orderedIds) => store.reorderConfig("tag", orderedIds)}
            onColor={(id, color) => store.updateConfigColor("tag", id, color)}
          />
        )}
        {catalog === "motivos" && (
          <CatalogEditor
            title="Motivos de perda"
            description="Explicações registradas ao encerrar uma participação como perdida."
            options={reasons.map((reason) => ({ id: reason.id, name: reason.name }))}
            archived={archivedReasons.map((reason) => ({ id: reason.id, name: reason.name }))}
            onCreate={(name) => store.addLossReason(name, funnelId)}
            onRename={(id, name) => store.renameConfig("lossReason", id, name)}
            onArchive={(id) => store.toggleArchivedConfig("lossReason", id)}
            onRestore={(id) => store.toggleArchivedConfig("lossReason", id)}
            onReorder={(orderedIds) => store.reorderConfig("lossReason", orderedIds)}
          />
        )}
        {catalog === "origens" && (
          <LeadOptionsEditor
            key="sources"
            title="Origens"
            description="De onde chegam os seus cadastros."
            kind="sources"
            values={sources}
            inUse={sourcesInUse}
          />
        )}
        {catalog === "segmentos" && (
          <LeadOptionsEditor
            key="segments"
            title="Segmentos"
            description="Ramo de atuação da empresa."
            kind="segments"
            values={segments}
            inUse={segmentsInUse}
          />
        )}
        {catalog === "classificacoes" && (
          <>
            <CatalogEditor
              title="Classificações"
              description="Eixos próprios para qualificar cada cadastro."
              options={classifications.map((item) => ({ id: item.id, name: item.name }))}
              archived={archivedClassifications.map((item) => ({ id: item.id, name: item.name }))}
              onCreate={async (name) => (await store.createClassification({ name })).ok}
              onRename={async (id, name) => (await store.renameClassification({ definitionId: id, name })).ok}
              onArchive={async (id) => (await store.archiveClassification({ definitionId: id, archived: true })).ok}
              onRestore={async (id) =>
                (await store.archiveClassification({ definitionId: id, archived: false })).ok
              }
              onReorder={async (orderedIds) => (await store.reorderClassifications({ orderedIds })).ok}
              onPurge={async (id) => (await store.removeClassification({ definitionId: id })).ok}
              references={(id) =>
                (store.leadClassificationValues ?? []).filter((value) => value.definitionId === id)
                  .length
              }
            />
            <ClassificationOptions eixo={eixo} onEixo={setEixo} />
          </>
        )}
        {catalog === "campos" && <FormFieldsEditor />}
      </div>
    </section>
  );
}

/**
 * Opções de um eixo de classificação.
 *
 * O eixo sozinho não classifica nada: sem opção cadastrada, o seletor do
 * cadastro do lead não tem o que oferecer e o eixo some do formulário. Por isso
 * as duas coisas vivem na mesma aba — o eixo em cima, as respostas dele aqui.
 */
function ClassificationOptions({ eixo, onEixo }: { eixo: string; onEixo: (id: string) => void }) {
  const store = useStore();
  const eixos = (store.classificationDefinitions ?? [])
    .filter((item) => !item.archived)
    .sort((left, right) => left.order - right.order);
  const atual = eixos.find((item) => item.id === eixo) ?? eixos[0];
  const options = (store.classificationOptions ?? [])
    .filter((item) => item.definitionId === atual?.id && !item.archived)
    .sort((left, right) => left.order - right.order);
  const archived = (store.classificationOptions ?? [])
    .filter((item) => item.definitionId === atual?.id && item.archived)
    .sort((left, right) => left.order - right.order);

  if (!atual) return null;

  return (
    <section className="cs-catalog-options">
      {eixos.length > 1 && (
        <nav className="chips" aria-label="Eixo de classificação">
          {eixos.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={item.id === atual.id}
              onClick={() => onEixo(item.id)}
            >
              {item.name}
            </button>
          ))}
        </nav>
      )}
      <CatalogEditor
        title={`Opções de ${atual.name}`}
        description="As respostas possíveis desse eixo, na ordem em que aparecem no cadastro."
        options={options.map((item) => ({ id: item.id, name: item.name }))}
        archived={archived.map((item) => ({ id: item.id, name: item.name }))}
        onCreate={async (name) =>
          (await store.createClassificationOption({ definitionId: atual.id, name })).ok
        }
        onRename={async (id, name) =>
          (await store.renameClassificationOption({ optionId: id, name })).ok
        }
        onArchive={async (id) =>
          (await store.archiveClassificationOption({ optionId: id, archived: true })).ok
        }
        onRestore={async (id) =>
          (await store.archiveClassificationOption({ optionId: id, archived: false })).ok
        }
        onReorder={async (orderedIds) =>
          (await store.reorderClassifications({ definitionId: atual.id, orderedIds })).ok
        }
        onPurge={async (id) => (await store.removeClassificationOption({ optionId: id })).ok}
        references={(id) =>
          (store.leadClassificationValues ?? []).filter((value) => value.optionId === id).length
        }
      />
    </section>
  );
}

interface CatalogOption {
  id: string;
  name: string;
  color?: string;
}

/**
 * Editor de catálogo em massa, como no protótipo: uma opção por linha.
 *
 * O texto é a lista inteira. Uma linha nova cria a opção; uma linha que sai
 * **arquiva** — nunca apaga —, porque o valor pode estar em leads, jornadas e
 * relatórios (§18.1), e é isso que a nota de rodapé promete. Como apagar uma
 * linha não parece destrutivo, a saída passa por confirmação nomeando o que
 * deixa as listas ativas, e o que foi arquivado volta pela lista logo abaixo.
 *
 * Renomear continua sendo comando próprio, no menu da própria opção: trocar o
 * texto de uma linha aqui é sair e entrar, e o ID — que a jornada referencia —
 * não sobreviveria a isso.
 */
function CatalogEditor({
  title,
  description,
  options,
  archived = [],
  note,
  readOnly = false,
  onCreate,
  onRename,
  onArchive,
  onRestore,
  onReorder,
  onColor,
  onPurge,
  references,
}: {
  title: string;
  description: string;
  options: CatalogOption[];
  archived?: CatalogOption[];
  note?: string;
  readOnly?: boolean;
  onCreate?: (name: string) => Promise<boolean>;
  onRename?: (id: string, name: string) => Promise<boolean>;
  onArchive?: (id: string) => Promise<boolean>;
  onRestore?: (id: string) => Promise<boolean>;
  onReorder?: (orderedIds: string[]) => Promise<boolean>;
  /** Presente só onde a cor é atributo do domínio, não preferência visual. */
  onColor?: (id: string, color: string) => Promise<boolean>;
  /** Exclusão definitiva; só existe onde o domínio sabe apagar de verdade. */
  onPurge?: (id: string) => Promise<boolean>;
  /** Quantos registros ainda apontam para a opção; zero libera a exclusão. */
  references?: (id: string) => number;
}) {
  const saved = options.map((option) => option.name).join("\n");
  const [draft, setDraft] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<CatalogOption[] | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  /** Arquivada que aguarda a confirmação nominal da exclusão definitiva. */
  const [purging, setPurging] = useState<CatalogOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bulk = draft ?? saved;
  const editable = Boolean(onCreate && onArchive);

  /** Linhas úteis: sem espaços nas pontas, sem vazias, sem repetição. */
  const parsed = () => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const line of bulk.split("\n")) {
      const name = line.trim();
      if (!name) continue;
      const key = name.toLocaleLowerCase("pt-BR");
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
    return names;
  };

  const apply = async (names: string[], archiving: CatalogOption[]) => {
    setBusy(true);
    setError("");
    let ok = true;
    const current = new Map(
      options.map((option) => [option.name.toLocaleLowerCase("pt-BR"), option.id]),
    );
    for (const name of names) {
      if (current.has(name.toLocaleLowerCase("pt-BR"))) continue;
      ok = (await onCreate?.(name)) !== false && ok;
    }
    for (const option of archiving) {
      ok = (await onArchive?.(option.id)) !== false && ok;
    }
    // A ordem das linhas é a ordem da lista: mover uma linha aqui precisa
    // mover a opção em todo lugar que a consome (§18.1). Só as opções que já
    // existiam entram — as criadas agora ainda não têm ID nesta closure, e o
    // comando de criação já as coloca no fim.
    const mantidas = names
      .map((name) => current.get(name.toLocaleLowerCase("pt-BR")))
      .filter((id): id is string => Boolean(id));
    const antes = options
      .filter((option) => mantidas.includes(option.id))
      .map((option) => option.id);
    if (onReorder && mantidas.join("\u0000") !== antes.join("\u0000")) {
      ok = (await onReorder(mantidas)) !== false && ok;
    }
    setBusy(false);
    setLeaving(null);
    if (ok) setDraft(null);
    else setError("Não foi possível salvar a lista inteira. Confira o que ficou e tente de novo.");
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editable) return;
    const names = parsed();
    const keep = new Set(names.map((name) => name.toLocaleLowerCase("pt-BR")));
    const archiving = options.filter(
      (option) => !keep.has(option.name.toLocaleLowerCase("pt-BR")),
    );
    if (archiving.length > 0) {
      setLeaving(archiving);
      return;
    }
    await apply(names, []);
  };

  const rename = async () => {
    if (!editing || !onRename) return;
    const name = editing.name.trim();
    if (!name) return;
    setBusy(true);
    const ok = await onRename(editing.id, name);
    setBusy(false);
    if (ok) setEditing(null);
    else setError("Não foi possível renomear. Tente novamente.");
  };

  return (
    <>
      <header>
        <h2>{title}</h2>
        <p>{description}</p>
      </header>
      {/* O formulário cobre o corpo e o rodapé: salvar fica no rodapé comum das
          configurações, com o estado à esquerda, como no protótipo. */}
      <form className="cs-catalog-form" onSubmit={save}>
        <div className="cs-catalog-body">
          {editable && (
            <div className="cs-catalog-bulk">
              <Field
                label="Opções disponíveis"
                help={`Uma opção por linha, na ordem em que devem aparecer${onReorder ? "" : " (a ordem desta lista não é salva)"}. Duplicatas e linhas vazias são ignoradas.`}
              >
                {({ id, describedBy }) => (
                  <TextArea
                    id={id}
                    aria-describedby={describedBy}
                    rows={8}
                    spellCheck={false}
                    value={bulk}
                    onChange={(event) => setDraft(event.target.value)}
                  />
                )}
              </Field>
            </div>
          )}

          <div className="cs-catalog-preview">
            <h3>
              Opções salvas<span>{options.length}</span>
            </h3>
            {options.length === 0 ? (
              <p className="cs-field-note">Nenhuma opção cadastrada ainda.</p>
            ) : (
              <ul>
                {options.map((option) => (
                  <li key={option.id}>
                    {editing?.id === option.id ? (
                      <span className="cs-option-edit">
                        <TextInput
                          value={editing.name}
                          maxLength={60}
                          aria-label={`Novo nome de ${option.name}`}
                          autoFocus
                          onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              void rename();
                            }
                            if (event.key === "Escape") setEditing(null);
                          }}
                        />
                        <Button size="compact" variant="primary" onClick={() => void rename()} loading={busy}>
                          Salvar
                        </Button>
                        <Button size="compact" onClick={() => setEditing(null)}>
                          Cancelar
                        </Button>
                      </span>
                    ) : (
                      <Menu
                        label={`Ações de ${option.name}`}
                        title={option.name}
                        sections={[
                          {
                            id: "acoes",
                            items: [
                              {
                                id: "renomear",
                                label: "Renomear",
                                icon: <Pencil size={14} aria-hidden="true" />,
                                disabled: !onRename,
                                disabledReason: onRename ? undefined : "Esta lista é somente leitura aqui.",
                              },
                              {
                                id: "arquivar",
                                label: "Arquivar",
                                icon: <Archive size={14} aria-hidden="true" />,
                                disabled: !onArchive,
                                disabledReason: onArchive
                                  ? undefined
                                  : "Esta lista é somente leitura aqui.",
                              },
                            ],
                          },
                        ]}
                        onSelect={(id) => {
                          if (id === "renomear") setEditing({ id: option.id, name: option.name });
                          if (id === "arquivar" && onArchive) void onArchive(option.id);
                        }}
                        trigger={(args) => (
                          <button {...args} type="button" className="cs-option-tag">
                            {option.name}
                          </button>
                        )}
                      />
                    )}
                    {/* A bolinha fica fora do gatilho do menu: são dois comandos
                        diferentes, e botão dentro de botão não é marcação válida. */}
                    {onColor && (
                      <ColorMenu
                        about={option.name}
                        value={option.color}
                        onChange={(color) => void onColor(option.id, color)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {error && (
              <p className="cs-form-error" role="alert">
                {error}
              </p>
            )}
          </div>

          {archived.length > 0 && onRestore && (
            <div className="cs-catalog-archived">
              <h3>
                Arquivadas<span>{archived.length}</span>
              </h3>
              <ul>
                {archived.map((option) => (
                  <li key={option.id}>
                    <span className="cs-option-tag is-archived">{option.name}</span>
                    <Button size="compact" variant="text" onClick={() => void onRestore(option.id)}>
                      Restaurar
                    </Button>
                    {onPurge && (
                      <Button
                        size="compact"
                        variant="text"
                        disabled={(references?.(option.id) ?? 0) > 0}
                        title={
                          (references?.(option.id) ?? 0) > 0
                            ? `Ainda em uso por ${references?.(option.id)} registro(s): apagar perderia histórico.`
                            : undefined
                        }
                        onClick={() => setPurging(option)}
                      >
                        Excluir definitivamente
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="cs-inline-note">
            <Lock size={14} aria-hidden="true" />
            <span>
              {note ??
                "Arquivar tira a opção das listas ativas e preserva o que já foi usado em leads, jornadas e relatórios."}
            </span>
          </p>
          {readOnly && options.length > 0 && (
            <p className="cs-field-note">Esta lista é somente leitura nesta tela.</p>
          )}
        </div>
        {editable && (
          <FormFoot
            dirty={bulk !== saved}
            saving={busy}
            onDiscard={() => setDraft(null)}
            saveLabel="Salvar opções"
          />
        )}
      </form>

      {purging && onPurge && (
        <ConfirmDialog
          title="Excluir definitivamente"
          question={`“${purging.name}” sai do sistema para sempre. Nenhum registro aponta para ela hoje, e não há como desfazer.`}
          confirmLabel="Excluir definitivamente"
          nominal={purging.name}
          danger
          onConfirm={async () => {
            const alvo = purging;
            setPurging(null);
            if (await onPurge(alvo.id))
              showFeedback(`“${alvo.name}” foi excluída definitivamente.`, "success");
            else showFeedback("Não foi possível excluir a opção.");
          }}
          onClose={() => setPurging(null)}
        />
      )}

      <Dialog
        open={leaving !== null}
        onClose={() => setLeaving(null)}
        title={`Arquivar ${leaving?.length === 1 ? "uma opção" : `${leaving?.length ?? 0} opções`}?`}
        description="Apagar a linha tira a opção das listas ativas. O que já foi usado em leads, jornadas e relatórios continua como está, e ela volta pela lista de arquivadas."
        footer={
          <>
            <Button onClick={() => setLeaving(null)}>Cancelar</Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => void apply(parsed(), leaving ?? [])}
            >
              Arquivar e salvar
            </Button>
          </>
        }
      >
        <ul className="cs-catalog-leaving">
          {(leaving ?? []).map((option) => (
            <li key={option.id}>{option.name}</li>
          ))}
        </ul>
      </Dialog>
    </>
  );
}

/**
 * Origens e Segmentos, no formato do protótipo: uma opção por linha, as opções
 * salvas logo abaixo e o salvar no rodapé comum.
 *
 * A lista é sugestão do cadastro, não enum (§14.4). Apagar uma linha só tira
 * a sugestão: os leads que já usam o valor continuam com ele. Valor novo
 * digitado num cadastro entra sozinho na lista (lead-options.ts).
 */
function LeadOptionsEditor({
  title,
  description,
  kind,
  values,
  inUse,
}: {
  title: string;
  description: string;
  kind: LeadOptionKind;
  values: string[];
  inUse: string[];
}) {
  const store = useStore();
  const saved = values.join("\n");
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bulk = draft ?? saved;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const current = readPreference(store.userPreferences ?? [], "leadOptions").value;
    const next = nextLeadOptions(current, inUse, kind, bulk.split("\n"));
    const result = await store.setPreference({ key: "leadOptions", value: next });
    setBusy(false);
    if (result.ok) setDraft(null);
    else setError(result.error.message);
  };

  return (
    <>
      <header>
        <h2>{title}</h2>
        <p>{description}</p>
      </header>
      <form className="cs-catalog-form" onSubmit={save}>
        <div className="cs-catalog-body">
          <div className="cs-catalog-bulk">
            <Field
              label="Opções disponíveis"
              help="Uma opção por linha. Edite, adicione ou remova; duplicatas e linhas vazias serão ignoradas."
            >
              {({ id, describedBy }) => (
                <TextArea
                  id={id}
                  aria-describedby={describedBy}
                  rows={8}
                  spellCheck={false}
                  value={bulk}
                  onChange={(event) => setDraft(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="cs-catalog-preview">
            <h3>
              Opções salvas<span>{values.length}</span>
            </h3>
            {values.length === 0 ? (
              <p className="cs-field-note">Nenhuma opção cadastrada ainda.</p>
            ) : (
              <ul>
                {values.map((value) => (
                  <li key={value}>
                    <span className="cs-option-tag">{value}</span>
                  </li>
                ))}
              </ul>
            )}
            {error && (
              <p className="cs-form-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <p className="cs-inline-note">
            <Lock size={14} aria-hidden="true" />
            <span>Valores já usados em leads e clientes são preservados.</span>
          </p>
        </div>
        <FormFoot dirty={bulk !== saved} saving={busy} onDiscard={() => setDraft(null)} saveLabel="Salvar opções" />
      </form>
    </>
  );
}

const SERVICES = [
  {
    id: "email",
    name: "E-mail",
    hint: "Correspondências e modelos na Inbox.",
    icon: <Mail size={21} aria-hidden="true" />,
    tone: "email",
  },
  {
    id: "wa",
    name: "WhatsApp",
    hint: "Conversas e materiais de atendimento.",
    icon: <Inbox size={21} aria-hidden="true" />,
    tone: "wa",
  },
  {
    id: "ig",
    name: "Instagram",
    hint: "Mensagens dos seus contatos.",
    icon: <Instagram size={21} aria-hidden="true" />,
    tone: "ig",
  },
  {
    id: "google",
    name: "Google Tasks e Calendar",
    hint: "Tarefas e compromissos da operação.",
    icon: <Calendar size={21} aria-hidden="true" />,
    tone: "email",
  },
];

function IntegracoesTab() {
  return (
    <section className="cs-panel">
      <div className="cs-integration-head">
        <h2>Contas e serviços</h2>
        <span className="cs-local">
          <Monitor size={15} aria-hidden="true" /> Demonstração local
        </span>
        <p>
          Explore as conversas e a organização do CRM com dados demonstrativos. As conexões reais ainda não estão
          disponíveis.
        </p>
      </div>
      <div className="cs-integrations">
        {SERVICES.map((service) => (
          <div className="cs-integration-row" key={service.id}>
            <span className="cs-service-icon" data-channel={service.tone}>
              {service.icon}
            </span>
            <div>
              <h3>{service.name}</h3>
              <p>{service.hint}</p>
            </div>
            <span className="cs-disconnected">Não conectado</span>
            <Button
              disabled
              title="A conexão exige o servidor de autenticação, que ainda não está disponível."
            >
              Conectar
            </Button>
          </div>
        ))}
      </div>
      <p className="cs-integration-note">
        <Lock size={15} aria-hidden="true" />
        <span>
          A conexão estará disponível quando houver autenticação segura. Nenhuma credencial é solicitada nesta
          demonstração.
        </span>
      </p>
    </section>
  );
}

function MetasTab() {
  const store = useStore();
  const navigate = useNavigate();
  const goals = store.revenueGoals ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const current = goals.find((goal) => goal.periodStart <= today && goal.periodEnd >= today) ?? goals.at(-1);
  const savedValue = current ? String(Math.round(current.amountCents / 100)) : "";
  const [value, setValue] = useState(savedValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setValue(savedValue), [savedValue]);

  const dirty = value.trim() !== savedValue;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const reais = Number(value.replace(/\D/g, ""));
    if (!Number.isFinite(reais) || reais <= 0) {
      setError("Informe um valor maior que zero.");
      return;
    }
    if (!current) {
      setError("Não há trimestre declarado para receber a meta.");
      return;
    }
    setSaving(true);
    setError("");
    const result = await store.setRevenueGoal({
      periodStart: current.periodStart,
      periodEnd: current.periodEnd,
      amountCents: reais * 100,
    });
    setSaving(false);
    if (!result.ok) setError(result.error.message);
  };

  return (
    <section className="cs-panel">
      <div className="cs-goal-header">
        <span className="cs-service-icon">
          <Target size={21} aria-hidden="true" />
        </span>
        <div>
          <h2>Meta trimestral de receita</h2>
          <p>Objetivo atual</p>
          <strong className="cs-goal-value num">
            {current ? money(Math.round(current.amountCents / 100)) : "—"}
          </strong>
        </div>
        <Button
          trailingIcon={<ArrowRight size={15} aria-hidden="true" />}
          onClick={() => navigate(appPath())}
        >
          Ver acompanhamento
        </Button>
      </div>
      <form className="cs-form" onSubmit={submit}>
        <section className="cs-setting-row">
          <div className="cs-section-intro">
            <h2>Definir objetivo</h2>
            <p>Este valor atualiza a meta de receita da tela inicial.</p>
          </div>
          <div className="cs-goal-fields">
            <Field label="Receita desejada (R$)" required error={error || undefined}>
              {({ id, describedBy, invalid }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  inputMode="numeric"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                />
              )}
            </Field>
            <p className="cs-field-note">
              A meta é um objetivo. Alterá-la não muda contratos, recebimentos ou o tempo decorrido do trimestre.
            </p>
          </div>
        </section>
        <FormFoot dirty={dirty} saving={saving} onDiscard={() => setValue(savedValue)} saveLabel="Salvar meta" />
      </form>
    </section>
  );
}

function IaTab() {
  const store = useStore();
  const navigate = useNavigate();
  const saved = readPreference(store.userPreferences ?? [], "ai").value;
  const [text, setText] = useState(saved.instructions);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setText(saved.instructions), [saved.instructions]);

  const dirty = text !== saved.instructions;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const result = await store.setPreference({ key: "ai", value: { instructions: text } });
    setSaving(false);
    if (!result.ok) setError(result.error.message);
  };

  return (
    <>
      <section className="cs-panel">
        <div className="cs-ai-header">
          <span>
            <CosmosAiIcon size={22} />
          </span>
          <div>
            <h2>Orientação do CosmosAI</h2>
            <p>Tom, contexto e limites para suas sugestões.</p>
          </div>
          <span className="cs-local">
            <Monitor size={15} aria-hidden="true" /> Sugestão manual, na Inbox
          </span>
        </div>
        <form className="cs-form" onSubmit={submit}>
          <section className="cs-setting-row">
            <div className="cs-section-intro">
              <h2>Instruções de escrita</h2>
              <p>Descreva como você quer se comunicar e o que deve ser evitado.</p>
            </div>
            <div>
              <Field label="Tom, contexto e restrições">
                {({ id }) => (
                  <TextArea
                    id={id}
                    value={text}
                    maxLength={2000}
                    rows={7}
                    onChange={(event) => setText(event.target.value)}
                  />
                )}
              </Field>
              <p className="cs-field-note">
                A orientação acompanha a sugestão manual da Inbox. Não escreva aqui chave, senha ou dado de acesso:
                isto é preferência, não credencial.
              </p>
            </div>
          </section>
          {error && (
            <p className="cs-form-error" role="alert">
              {error}
            </p>
          )}
          <FormFoot dirty={dirty} saving={saving} onDiscard={() => setText(saved.instructions)} saveLabel="Salvar instruções" />
        </form>
        <div className="cs-review-note">
          <Check size={16} aria-hidden="true" />
          <div>
            <strong>Revisão humana antes de enviar</strong>
            <p>Uma sugestão só entra na conversa quando você a aplica e confirma o envio.</p>
          </div>
        </div>
      </section>

      <section className="cs-panel cs-library-link">
        <div>
          <FileText size={20} aria-hidden="true" />
          <div>
            <h2>Modelos de mensagem</h2>
            <p>Gerencie os textos e as variáveis que você usa no atendimento.</p>
          </div>
        </div>
        <Button
          trailingIcon={<ArrowRight size={15} aria-hidden="true" />}
          onClick={() => navigate(appPath("modelos"))}
        >
          Abrir biblioteca
        </Button>
      </section>
    </>
  );
}
